import {
  type LeadListQueryParams,
  useInfiniteLeadListQuery,
} from "Api/Queries/Lead/useGetLeadListQuery";
import { LeadList } from "Components/Affiliate/Stats/LeadList";
import { CollapsiblePaper } from "Components/Shared/CollapsiblePaper/CollapsiblePaper";
import { LeadStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Affiliate.AffiliateStats;

const LEAD_STATUS_TO_DATE_VARIANT: Record<LeadStatus, "created" | "updated"> = {
  [LeadStatus.Invited]: "created",
  [LeadStatus.Completed]: "updated",
  [LeadStatus.Registered]: "created",
  [LeadStatus.CompletedRewardSent]: "updated",
  [LeadStatus.CompletedRewardApplied]: "updated",
  [LeadStatus.AnonymousPaymentPerformed]: "created",
  [LeadStatus.Withdrawn]: "updated",
};

const LEAD_STATUS_TO_TITLE_TRANSLATION_KEY: Record<LeadStatus, string> = {
  [LeadStatus.Invited]: RESOURCES.Invited,
  [LeadStatus.Registered]: RESOURCES.Registered,
  [LeadStatus.Completed]: RESOURCES.WithActiveLicense,
  [LeadStatus.AnonymousPaymentPerformed]: RESOURCES.AnonymousPayment,
  [LeadStatus.CompletedRewardSent]: "-",
  [LeadStatus.CompletedRewardApplied]: "-",
  // ZS-1623: a referral revoked by leaving Díky Tobě is never listed.
  [LeadStatus.Withdrawn]: "-",
};

type Props = LeadListQueryParams;

export const FilteredLeadList = ({
  registeredAsRole,
  leadStatus,
  invitedAsRole,
  limit,
}: Props) => {
  const { t } = useResource();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteLeadListQuery({
      registeredAsRole,
      leadStatus,
      invitedAsRole,
      limit,
    });

  const totalCount = data?.pages?.[0]?.totalCount ?? 0;
  const items = data?.pages?.flatMap(({ items }) => items) ?? [];

  const title = leadStatus
    ? t(LEAD_STATUS_TO_TITLE_TRANSLATION_KEY[leadStatus])
    : "-";

  const dateVariant = leadStatus
    ? LEAD_STATUS_TO_DATE_VARIANT[leadStatus]
    : "created";

  return (
    <CollapsiblePaper
      title={title}
      chipCount={totalCount}
      isLoading={isLoading}
    >
      {totalCount > 0 && (
        <LeadList
          items={items}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          dateVariant={dateVariant}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      )}
    </CollapsiblePaper>
  );
};
